import React, { useMemo } from 'react';
import { OptionType, useModal } from '@europrocurement/flexy-components';
import {
    useCurrentDossierCtx,
    useDossierDocumentsCtx,
    useDossierProcesses,
} from '@europrocurement/l2d-modules';
import { enqueueSnackbar } from 'notistack';
import DissociateFormalitiesForm from '../components/DissociateFormalitiesForm/DissociateFormalitiesForm';
import MoveFormalitiesForm from '../components/MoveFormalitiesToOtherDossierForm/MoveFormalitiesToOtherDossierForm';

type UseDossierBulkActionsOptions = {
    selectedIds: number[];
    refreshDossier: () => void;
    /**
     * 'article'  — selectedIds and passed ids are article IDs, converted to formality IDs internally (default)
     * 'formality' — selectedIds and passed ids are already formality IDs, no conversion needed
     */
    idType?: 'article' | 'formality';
};

const useDossierBulkActions = ({
    selectedIds,
    refreshDossier,
    idType = 'article',
}: UseDossierBulkActionsOptions) => {
    const { modalActions } = useModal();
    const { dossier, siblings } = useCurrentDossierCtx();
    const { getDossierDocuments } = useDossierDocumentsCtx();
    const { hasOnGoingProcesses } = useDossierProcesses();

    // Retrieve formalities from article as the dissocier/ deplacer will update the presta
    const getFormalityIdsFromArticleIds = (articleIds: number[]): number[] => {
        if (!dossier) return [];
        const formalityIds = new Set<number>();
        articleIds.forEach((articleId) => {
            const formality = dossier.formalites.find((f) => f.articleIds.includes(articleId));
            if (formality?.id != null) {
                formalityIds.add(formality.id);
            }
        });
        return Array.from(formalityIds);
    };

    const resolveFormalityIds = (ids: number[]): number[] =>
        idType === 'article' ? getFormalityIdsFromArticleIds(ids) : ids;

    // Remove current dossier and deleted dossier from siblings
    const filteredSiblings = useMemo(
        () =>
            (siblings &&
                dossier &&
                siblings.filter((doss) => doss.id !== dossier.id && doss.deleted === false)) ||
            [],
        [dossier, siblings],
    );

    const canDissociate = useMemo(() => {
        const effectiveIds = resolveFormalityIds(selectedIds);
        const leftPrestations = (dossier?.formalites || [])
            .map((formality) => formality.id)
            .flat()
            .filter((prestationId) => !effectiveIds.includes(prestationId));

        if ((dossier?.formalites?.length ?? 0) <= 1) {
            return false;
        }

        return leftPrestations.length > 0;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier, selectedIds]);
    const canMove = useMemo(() => filteredSiblings.length > 0, [filteredSiblings]);

    /**
     * dissociate prestation from articleIds or prestaIds
     *
     * @param ids is optionnal as the ids can came from the param or the selectedIds (checkboxes) passed in the hook
     * @returns void
     */
    const handleDissociate = (ids?: number[]) => {
        if (!dossier) return;

        if (hasOnGoingProcesses(dossier.validationProcesses)) {
            enqueueSnackbar(
                'Des processus de validation sont en cours sur le dossier, dissociation impossible',
                { variant: 'error' },
            );
            return;
        }

        const effectiveIds = selectedIds.length > 0 ? selectedIds : ids;
        if (!effectiveIds) return;

        modalActions.call(
            <DissociateFormalitiesForm
                formalitiesIds={resolveFormalityIds(effectiveIds)}
                dossier={dossier}
                documents={getDossierDocuments()}
                onSubmitDone={refreshDossier}
            />,
        );
    };

    /**
     * move/ déplacer prestation from articleIds or prestaIds
     *
     * @param ids is optionnal as the ids can came from the param or the selectedIds (checkboxes) passed in the hook
     * @returns void
     */
    const handleMove = (ids?: number[]) => {
        if (!dossier) return;

        if (hasOnGoingProcesses(dossier.validationProcesses)) {
            enqueueSnackbar(
                'Des processus de validation sont en cours sur le dossier, déplacement impossible',
                { variant: 'error' },
            );
            return;
        }

        const effectiveIds = selectedIds.length > 0 ? selectedIds : ids;
        if (!effectiveIds) return;

        const options = filteredSiblings.map((doss) => {
            const nbrFormalities = doss.formalites.length;
            return {
                value: doss.id,
                label: `${doss.numero} - ${doss.societeDenomination} - ${
                    nbrFormalities > 1
                        ? `${doss.formalites[0].libelle} + ${nbrFormalities - 1} `
                        : doss.formalites[0].libelle
                }`,
            } as OptionType;
        });

        modalActions.call(
            <MoveFormalitiesForm
                formalitiesIds={resolveFormalityIds(effectiveIds)}
                dossier={dossier}
                documents={getDossierDocuments()}
                onSubmitDone={refreshDossier}
                options={options}
            />,
        );
    };

    return {
        canDissociate,
        canMove,
        handleDissociate,
        handleMove,
    };
};

export default useDossierBulkActions;
